import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, ChevronDown, Twitch, Youtube, Video } from "lucide-react";
import { api } from "../lib/api";
import { buildStreamEmbed } from "../lib/streamEmbed";
import { PartnerBadge } from "./PartnerBadge";

const PLATFORM = {
  twitch: { label: "Twitch", color: "#9146FF", Icon: Twitch },
  youtube: { label: "YouTube", color: "#FF0000", Icon: Youtube },
  kick: { label: "Kick", color: "#53FC18", Icon: Video },
  tiktok: { label: "TikTok", color: "#25F4EE", Icon: Video },
  other: { label: "Stream", color: "#D4A94A", Icon: Video },
};

const LiveStreamers = () => {
  const [streamers, setStreamers] = useState([]);
  const [open, setOpen] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/streamers/live");
      setStreamers(data.streamers || []);
    } catch {
      setStreamers([]);
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 45000);
    return () => clearInterval(timer.current);
  }, []);

  if (!streamers.length) return null;

  return (
    <div className="relative" data-testid="live-streamers">
      <button
        data-testid="live-streamers-pill"
        onClick={() => setOpen((o) => !o)}
        className="h-9 inline-flex items-center gap-1.5 px-2.5 md:px-3 bg-[#13151B]/80 border border-[#E4443A]/50 hover:border-[#E4443A] tactical-frame-sm transition-colors"
        title={`${streamers.length} operator${streamers.length > 1 ? "s" : ""} streaming from the server`}
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E4443A] opacity-70" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E4443A]" />
        </span>
        <Radio className="w-3.5 h-3.5 text-[#E4443A]" />
        <span className="font-stencil font-black uppercase text-xs text-[#F1F5F9]">
          Live <span className="text-[#E4443A]">· {streamers.length}</span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-[#94A3B8] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-[45]" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.98 }}
              transition={{ duration: 0.16 }}
              data-testid="live-streamers-dropdown"
              className="!absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-1rem)] origin-top-right glass tactical-frame z-50 p-1.5"
            >
              <div className="px-3 py-2 border-b border-[#2A2E3D] mb-1">
                <div className="overline text-[#E4443A]"><Radio className="inline w-3 h-3 mr-1" /> Streaming from the server</div>
              </div>
              {streamers.map((s) => {
                const p = PLATFORM[s.platform] || PLATFORM.other;
                const preview = buildStreamEmbed({ url: s.stream_url, platform: s.platform, channelId: s.channel_id, muted: true, autoplay: true });
                const showPreview = previewId === s.user_id && !!preview;
                return (
                  <Link
                    key={s.user_id}
                    to={s.is_partner && s.partner_login ? `/streamers/${s.partner_login}` : `/watch/${s.user_id}`}
                    data-testid={`live-streamer-${s.user_id}`}
                    onClick={() => setOpen(false)}
                    onMouseEnter={() => setPreviewId(s.user_id)}
                    onMouseLeave={() => setPreviewId((cur) => (cur === s.user_id ? null : cur))}
                    className="block px-2 py-2 hover:bg-[#1A1D26] tactical-frame-sm transition-colors group"
                  >
                    {(s.thumbnail_url || preview) && (
                      <div className="relative mb-2 overflow-hidden tactical-frame-sm border border-[#2A2E3D]">
                        {showPreview ? (
                          <div className="relative w-full aspect-video bg-black" data-testid={`live-streamer-preview-${s.user_id}`}>
                            <iframe
                              src={preview}
                              title={`${s.callsign} live preview`}
                              className="absolute inset-0 w-full h-full pointer-events-none"
                              frameBorder="0"
                              allow="autoplay; encrypted-media; picture-in-picture"
                              scrolling="no"
                            />
                          </div>
                        ) : (
                          s.thumbnail_url && (
                            <img
                              src={s.thumbnail_url}
                              alt=""
                              data-testid={`live-streamer-thumb-${s.user_id}`}
                              className="w-full aspect-video object-cover group-hover:scale-[1.03] transition-transform"
                              loading="lazy"
                            />
                          )
                        )}
                        <span className="absolute top-1 left-1 inline-flex items-center gap-1 font-stencil font-black uppercase text-[8px] text-white bg-[#E4443A] px-1.5 py-0.5 z-10">
                          <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" /></span>
                          Live
                        </span>
                        {typeof s.viewer_count === "number" && (
                          <span data-testid={`live-streamer-viewers-${s.user_id}`} className="absolute bottom-1 right-1 font-mono text-[9px] text-white bg-black/70 px-1.5 py-0.5 z-10">
                            {s.viewer_count.toLocaleString()} watching
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2.5 px-1">
                      <span className={`relative shrink-0 ${s.is_partner ? "partner-avatar-ring tactical-frame-sm" : ""}`} style={s.is_partner ? { padding: 1 } : undefined}>
                        <img
                          src={s.avatar_url || `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${s.callsign}`}
                          alt=""
                          className="w-8 h-8 object-cover tactical-frame-sm border border-[#2A2E3D] block"
                        />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-stencil font-bold uppercase text-xs text-[#F1F5F9] group-hover:text-[#D4A94A] truncate flex items-center gap-1">
                          <span className="truncate">{s.clan_tag && <span className="text-[#94A3B8]">[{s.clan_tag}] </span>}{s.callsign}</span>
                          {s.is_partner && <PartnerBadge size={13} className="shrink-0" />}
                        </div>
                        <div className="font-mono text-[10px] flex items-center gap-1 min-w-0" style={{ color: p.color }}>
                          <p.Icon className="w-3 h-3 shrink-0" />
                          <span className="truncate">{s.game_name || p.label}</span>
                          {!s.thumbnail_url && typeof s.viewer_count === "number" && (
                            <span data-testid={`live-streamer-viewers-${s.user_id}`} className="text-[#94A3B8] shrink-0">· {s.viewer_count.toLocaleString()} watching</span>
                          )}
                        </div>
                      </div>
                      <span className="font-stencil font-black uppercase text-[9px] text-[#E4443A] border border-[#E4443A]/40 px-1.5 py-0.5 tactical-frame-sm shrink-0">Watch</span>
                    </div>
                  </Link>
                );
              })}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveStreamers;
